import React, { useMemo } from 'react';
import { useParams, useLocation } from 'react-router';

import { FiveHundredErrorPage } from '@sm/webassets';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import { usePricingExperience } from '~shared/hooks/usePricingExperience';
import usePackageSet from '../hooks/usePackageSet';
import { PackageType } from '~shared/types/pricing/package';
import { logAmplitudeEvent, logPricingPageLoad } from '~pricing/pages/shared/index';
import { getPackagesBasedOnQueryStringIds } from '~pricing/lib/lib-helpers';
import { PackagesProvider } from '~shared/hooks/usePackages';
import { MatchParams } from './types';
import { AMPLITUDE_PAGE_EVENTS } from '~shared/util';

export function withPageHandler<P>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> {
  return props => {
    const { canonicalPath = PackageType.Teams } = useParams<MatchParams>();
    const { search } = useLocation();
    const { packageSet } = usePackageSet(canonicalPath);

    const query = new URLSearchParams(search);
    const comparePlans = query.get('compare_plans');
    const featureId = query.get('selected_feature');
    const categoryId = query.get('selected_category');

    const { pageName, utSource } = useTrackingDetails();

    useMemo(() => {
      logPricingPageLoad({ pageName, utSource });
    }, [pageName, utSource]);

    useMemo(() => {
      if (categoryId || featureId) {
        logAmplitudeEvent({
          eventName: `selected ${featureId ? 'feature' : 'category'} from upgrade cta`,
          pageName,
          amplitudeEvent: AMPLITUDE_PAGE_EVENTS.experimentEvent,
          otherData: {
            [`selected${categoryId ? 'Category' : 'Feature'}`]: categoryId ?? featureId ?? '',
          },
        });
      }
    }, [categoryId, featureId, pageName]);

    const { packages } = usePricingExperience();

    // no data is no page
    const error = !Array.isArray(packages) || !packages.length;

    return (
      <Choose>
        {/* if there was an issue loading the data from pricingsvc, we've got nothing to show. bail */}
        <When condition={error}>
          <FiveHundredErrorPage />
        </When>
        <When condition={!error && packages.length}>
          <PackagesProvider
            packagesToDisplay={
              comparePlans ? getPackagesBasedOnQueryStringIds(comparePlans, canonicalPath) : packageSet
            }
            showEduDiscount={canonicalPath === PackageType.Educational}
            comparePlans={Boolean(comparePlans)}
          >
            <WrappedComponent {...props} />
          </PackagesProvider>
        </When>
      </Choose>
    );
  };
}

export default withPageHandler;
